import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { StepWizardChildProps } from 'react-step-wizard';
import { FormField } from '@dtp/membership-service-types';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { Text, TextVariant } from '@naf/text';
import { SanityBlock } from '@dtp/membership-service-types/dist/sanity/sanity';
import { useLoginRedirect } from '@hooks/useLoginRedirect';
import { LoginComponent } from '@components/LoginComponent';
import { Form } from '@components/Form';
import { useQueryParams } from '@hooks/useQueryParameters';
import { LoginComponent as LoginComponentData } from '../FormBlock.types';
import BlockContent from '../../../../BlockContent';
import { StyledUserInteraction } from './StyledUserInteraction';
import { SubmitButton, FormFieldsWrapper, TextField } from './FormStep.styles';

interface FormStepProps extends Partial<StepWizardChildProps> {
  fieldComponents: FormField[];
  textField?: SanityBlock[];
  title?: string;
  body?: SanityBlock[];
  control: UseFormReturn['control'];
  errors: UseFormReturn['formState']['errors'];
  clearErrors: UseFormReturn['clearErrors'];
  handleSubmit: UseFormReturn['handleSubmit'];
  getValues: UseFormReturn['getValues'];
  setValue: UseFormReturn['setValue'];
  watch: UseFormReturn['watch'];
  buttonText?: string;
  onSubmit: (data: FieldValues) => Promise<boolean>;
  shouldConfirm: boolean;
  hasReceiptPage: boolean;
  isSubmitting: boolean;
  errorState?: string | null;
  isSuccess: boolean;
  setError: UseFormReturn['setError'];
  isLoginBlocked?: boolean;
  loginComponent?: LoginComponentData;
}

export const FormStep: FC<FormStepProps> = ({
  nextStep,
  fieldComponents,
  textField,
  title,
  body,
  control,
  getValues,
  setValue,
  watch,
  errors,
  clearErrors,
  handleSubmit,
  buttonText,
  onSubmit,
  shouldConfirm,
  hasReceiptPage,
  isSubmitting,
  errorState,
  isSuccess,
  setError,
  isLoginBlocked,
  loginComponent,
}) => {
  const { redirectToLoginPage } = useLoginRedirect();

  const handleLoginClick = useCallback(() => {
    redirectToLoginPage(window.location.pathname + window.location.search);
  }, [redirectToLoginPage]);

  const onNext = useCallback(async () => {
    if (nextStep && hasReceiptPage) {
      if (!shouldConfirm) {
        const isSubmitted = await onSubmit(getValues());
        if (isSubmitted) {
          nextStep();
        }
      } else {
        nextStep();
      }
    } else {
      const data = getValues();
      onSubmit(data);
    }
  }, [nextStep, hasReceiptPage, shouldConfirm, getValues, onSubmit]);

  const queryParams = useQueryParams();
  const getQueryParamInitialValue = useMemo(
    () => (fieldId: string) =>
      queryParams && queryParams[fieldId] && typeof queryParams[fieldId] === 'string'
        ? (queryParams[fieldId] as string)
        : null,
    [queryParams],
  );

  useEffect(() => {
    if (fieldComponents && setValue) {
      fieldComponents.forEach((fieldComponent) => {
        if (fieldComponent.key === 'fieldComponent') {
          const queryInitialValue = getQueryParamInitialValue(fieldComponent.id);
          setValue(fieldComponent.id, fieldComponent.initialValue || queryInitialValue);
        } else if (fieldComponent.key === 'groupFields') {
          fieldComponent?.fieldComponents?.forEach((groupField) => {
            const queryInitialValue = getQueryParamInitialValue(fieldComponent.id);
            setValue(groupField.id, groupField.initialValue || queryInitialValue);
          });
        }
      });
    }
  }, [getQueryParamInitialValue, fieldComponents, setValue, queryParams]);

  return (
    <>
      <Text variant={TextVariant.Header2}>{title}</Text>
      {body ? <BlockContent value={body} /> : null}
      {isLoginBlocked && loginComponent && (
        <LoginComponent
          header={loginComponent.header}
          body={loginComponent.body}
          buttonText={loginComponent.buttonText}
          onLogin={handleLoginClick}
        />
      )}
      <FormFieldsWrapper $hasBody={Boolean(body?.length)}>
        <Form
          fieldComponents={fieldComponents}
          control={control}
          errors={errors}
          clearErrors={clearErrors}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          watch={watch}
          setValue={setValue}
          setError={setError}
        />
      </FormFieldsWrapper>
      {textField && textField.length !== 0 && (
        <TextField>
          <BlockContent value={textField} />
        </TextField>
      )}
      {!isSuccess && (
        <SubmitButton
          type="submit"
          onClick={handleSubmit(onNext)}
          disabled={isLoginBlocked || Object.values(errors).length !== 0}
          isLoading={isSubmitting}
        >
          {buttonText}
        </SubmitButton>
      )}
      {errorState && (
        <StyledUserInteraction
          title="Oops, nå kjørte vi oss fast"
          button={{
            type: 'submit',
            onClick: handleSubmit(onNext),
            children: 'Prøv igjen',
          }}
          variant="error"
        >
          {errorState}
        </StyledUserInteraction>
      )}
      {isSuccess && !errorState && !shouldConfirm && !hasReceiptPage && (
        <StyledUserInteraction title="Skjema er sendt inn">
          Takk for din henvendelse. Du får snart en bekreftelse på epost.
        </StyledUserInteraction>
      )}
    </>
  );
};
